import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  TextInput,
  Flex,
  Col,
  Select,
  MultiSelect,
  Switch,
  Group,
  Text,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import ApiLinks from '../../Navlinks/ApiLinks';
import LoaderOverlay from '../../Utilities/LoadingOverLay';
import ImageUploadWithPreview from '../../Utilities/ImageUploadWithPreview';
import { base64DecodeUnicode, base64EncodeUnicode } from '../../Utilities/OtherBasicFunction';

function OverviewTab({ id, setAmenities, setSpecification, setMap, setLandmarks }) {
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();

  // States for loading, image, editor content and select options.
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(''); // Holds decrypted propertyconfiguration content.
  const [stateOptions, setStateOptions] = useState([]);
  const [developerOptions, setDeveloperOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]); // New state for cities.
  const [selectedFile, setSelectedFile] = useState(null);
  const [existingImagePath, setExistingImagePath] = useState(null);

  // Initialize form with fields matching the API payload.
  const form = useForm({
    initialValues: {
      id: '',
      compcode: user?.compcode || '',
      propertyname: '',
      propertyaddress: '',
      propertystateid: '',
      propertycityid: '',
      developerid: '',
      propertylistdate: '', // Date (to be formatted as YYYY-MM-DD)
      category: [], // Keep as an array.
      rerano: '',
      pricerangefrom: '',
      pricerangeto: '',
      propertyconfiguration: '', // Not used directly; content from ReactQuill is used.
      arearangefrom: '',
      arearangeto: '',
      projectstatus: '',
      possessionon: '',
      projecttype: '',
      propertyoverview: '',
      propertyspecification: '',
      downloadbrochure: '',
      downloadpricelist: '',
      downloadmap: '',
      amenities: '',
      landmarks: '',
      map: '',
      propertystatus: true,
      sortorder: 0,
    },
    validate: {
      propertyname: (value) =>
        value.trim() !== '' ? null : 'Project Name is required',
      propertyaddress: (value) =>
        value.trim() !== '' ? null : 'Property Address is required',
      propertystateid: (value) => (value ? null : 'State is required'),
      propertycityid: (value) => (value ? null : 'City is required'),
      developerid: (value) => (value ? null : 'Developer is required'),
      propertylistdate: (value) =>
        value ? null : 'Property List Date is required',
      possessionon: (value) =>
        value ? null : 'Possession is required',
      projecttype: (value) => (value ? null : 'Project Type is required'),
    },
  });

  // ReactQuill toolbar configuration.
  const modules = {
    toolbar: [
      [{ font: [] }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ header: [1, 2, 3, 4, 5, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      ['code'],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['link', 'image', 'video'],
      ['clean'],
    ],
  };

  const formats = [
    'font',
    'size',
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'script',
    'code',
    'list',
    'bullet',
    'indent',
    'align',
    'link',
    'image',
    'video',
    'clean',
  ];

  // Load existing project details if id exists.
  useEffect(() => {
    if (!id) return;
    setLoading(true);

    axios
      .get(
        `${process.env.REACT_APP_API_URL}/${ApiLinks.ProjectOverview_GetById}/${id}/${user.compcode}`
      )
      .then((res) => {
        if (res.data.status && res.data.data.length > 0) {
          const d = res.data.data[0];

          // 1) parse the JSON category string into a JS array
          const categoryArray = d.category
            ? JSON.parse(d.category)
            : [];

          // 2) load the cities for this state so propertycityid has something to bind against
          //    note: loadCities expects a string
          loadCities(d.propertystateid.toString());

          // 3) now set all of your form values
          form.setValues({
            id: d.id,
            compcode: d.compcode,
            propertyname: d.propertyname,
            propertyaddress: d.propertyaddress,
            propertystateid: d.propertystateid.toString(),
            propertycityid: d.propertycityid.toString(),
            developerid: d.developerid.toString(),
            propertylistdate: d?.propertylistdate ? new Date(d.propertylistdate) : null,
            category: categoryArray,
            rerano: d.rerano,
            pricerangefrom: d.pricerangefrom,
            pricerangeto: d.pricerangeto,
            arearangefrom: d.arearangefrom,
            arearangeto: d.arearangeto,
            projectstatus: d.projectstatus,
            possessionon: d.possessionon,
            projecttype: d.projecttype,
            propertyoverview: d.propertyoverview,
            propertyspecification: d.propertyspecification,
            propertyconfiguration: d.propertyconfiguration,
            downloadbrochure: d.downloadbrochure,
            downloadpricelist: d.downloadpricelist,
            downloadmap: d.downloadmap,
            amenities: d.amenities,
            landmarks: d.landmarks,
            map: d.map,
            propertystatus: d.propertystatus ?? true, // ✅ use nullish coalescing
            sortorder: d.sortorder ?? 0,
          });

          // decrypt & set editor content
          const decrypted = d.propertyoverview
            ? base64DecodeUnicode(d.propertyoverview)
            : '';
          setContent(decrypted);
          // set the existing image preview
          setExistingImagePath(`${process.env.REACT_APP_IMG_URL}/${d.mainimages}`);

          // set ametine prop used in amentiesatb
          const fetchedAmenities = d.amenities;
          let amenityArray = [];
          if (fetchedAmenities?.startsWith('[')) {
            // already JSON Array
            amenityArray = JSON.parse(fetchedAmenities);
          } else if (fetchedAmenities) {
            // string comma separated
            amenityArray = fetchedAmenities.split(',').map(x => x.trim());
          }
          setAmenities(amenityArray);
          // set specification prop used in specificationtab
          setSpecification(d.propertyspecification);
          // set map prop used in map tab
          setMap(d.map);
          // set landmarks prop used in map tab
          setLandmarks(d.landmarks);

        }
      })
      .catch((err) => {
        console.error('Error loading project:', err);
      })
      .finally(() => setLoading(false));
  }, [id, user.compcode]);


  // Fetch options for State, Developer, and Category.
  useEffect(() => {
    // Fetch state options.
    axios
      .get(`${process.env.REACT_APP_API_URL}/${ApiLinks.State_list}/${user.compcode}`)
      .then((response) => {
        if (response.data.status) {
          const options = response.data.data.map((state) => ({
            value: state.stateid.toString(),
            label: state.statename,
          }));
          setStateOptions(options);
        }
      })
      .catch((error) => console.error('Error fetching states:', error));

    // Fetch developer options.
    axios
      .get(`${process.env.REACT_APP_API_URL}/${ApiLinks.Developer}/${user.compcode}`)
      .then((response) => {
        if (response.data.status) {
          const options = response.data.data.map((dev) => ({
            value: dev.developerid.toString(),
            label: dev.devname,
          }));
          setDeveloperOptions(options);
        }
      })
      .catch((error) => console.error('Error fetching developers:', error));

    // Fetch category options.
    axios
      .get(`${process.env.REACT_APP_API_URL}/${ApiLinks.Category}/${user.compcode}`)
      .then((response) => {
        if (response.data.status) {
          const options = response.data.data.map((cat) => ({
            value: cat.id.toString(),
            label: cat.categoryname,
          }));
          setCategoryOptions(options);
        }
      })
      .catch((error) => console.error('Error fetching categories:', error));
  }, [user.compcode]);

  // Function to load cities based on selected state.
  const loadCities = (stateId) => {
    if (!stateId) return;
    axios
      .get(`https://realestateapi.datamanindia.com/api/v2/city/GetAll/${user.compcode}/${stateId}`)
      .then((response) => {
        if (response.data.status && response.data.data) {
          const options = response.data.data.map((city) => ({
            value: city.cityid.toString(), // value is cityid
            label: city.cityname,          // label is cityname
          }));
          setCityOptions(options);
        } else {
          setCityOptions([]);
        }
      })
      .catch((error) => {
        console.error('Error fetching cities:', error);
        setCityOptions([]);
      });
  };

  // Handle form submission.
  const handleSubmit = (values) => {

    const errors = form.validate();
    if (Object.keys(errors.errors).length > 0) {
      toast.error('Please fill in all required fields.');
      return;
    }

    // Format dates as YYYY-MM-DD.
    const formattedPropertyListDate = values.propertylistdate
      ? new Date(values.propertylistdate).toISOString().slice(0, 10)
      : '';
    // Create FormData payload.
    const formData = new FormData();
    formData.append('propertyid', id || 0);
    formData.append('compcode', values.compcode);
    formData.append('propertyname', values.propertyname.trim());
    formData.append('propertyaddress', values.propertyaddress.trim());
    formData.append('propertystateid', values.propertystateid);
    formData.append('propertycityid', values.propertycityid);
    formData.append('developerid', values.developerid);
    if (selectedFile) {
      formData.append('mainimages', selectedFile);
    }
    formData.append('propertylistdate', formattedPropertyListDate);
    // Send category as a JSON string so that the API receives an array (e.g. [2,3,4]).
    formData.append('category', JSON.stringify(values.category));
    formData.append('rerano', values.rerano);
    formData.append('pricerangefrom', values.pricerangefrom);
    formData.append('pricerangeto', values.pricerangeto);
    formData.append('propertyconfiguration', values.propertyconfiguration);
    formData.append('arearangefrom', values.arearangefrom);
    formData.append('arearangeto', values.arearangeto);
    formData.append('projectstatus', values.projectstatus);
    formData.append('possessionon', values.possessionon);
    formData.append('projecttype', values.projecttype);
    formData.append('propertyoverview', base64EncodeUnicode(content));
    formData.append('propertyspecification', values.propertyoverview);
    formData.append('downloadbrochure', values.downloadbrochure);
    formData.append('downloadpricelist', values.downloadpricelist);
    formData.append('downloadmap', values.downloadmap);
    formData.append('amenities', values.amenities);
    formData.append('landmarks', values.landmarks);
    formData.append('map', values.map);
    formData.append('propertystatus', values.propertystatus);
    formData.append('sortorder', values.sortorder);
    setLoading(true);
    // IMPORTANT: Notice that the endpoints in ApiLinks have been swapped.
    // For new records (no id), we want to POST to the "Add" endpoint (property/Add),
    // which is mapped to ProjectOverview_Update.
    // For updates (id exists), we call the "Update" endpoint (property/Update), which is mapped to ProjectOverview_Save.
    const request = id && id !== 0
      // ? `${process.env.REACT_APP_API_URL}/${ApiLinks.ProjectOverview_Save}`
      // : `${process.env.REACT_APP_API_URL}/${ApiLinks.ProjectOverview_Update}`;

      ? axios.put(`${process.env.REACT_APP_API_URL}/${ApiLinks.ProjectOverview_Update}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      : axios.post(`${process.env.REACT_APP_API_URL}/${ApiLinks.ProjectOverview_Save}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });


    request.then((res) => {
      if (res.data.status) {
        Swal.fire({
          title: id ? 'Updated!' : 'Created!',
          text: `Project overview ${id ? 'updated' : 'created'} successfully.`,
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          navigate('/dashboard/property');
        });
      } else {
        Swal.fire('Error!', res.data.message || 'Failed to save.', 'error');
      }
    })
      .catch((error) => {
        Swal.fire('Error!', 'An error occurred while saving.', 'error');
        console.error('API error: ', error);
      })
      .finally(() => setLoading(false));
  };

  if (loading) return <LoaderOverlay />;

  return (
    <Box>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Grid gutter="md">
          {/* Row 1: Basic Info */}
          <Col span={12} md={4}>
            {/* Custom label with switch */}
            <Group justify="space-between" mb={4}>
              <Text size="sm" fw={500}>
                Project Name *
              </Text>
              <Switch
                size="sm"
                onLabel="Active"
                offLabel="In Active"
                {...form.getInputProps('propertystatus', { type: 'checkbox' })}
              />
            </Group>

            {/* Input field */}
            <TextInput
              placeholder="Enter project name"
              {...form.getInputProps('propertyname')}
            />
          </Col>

          <Col span={12} md={2}>
            <Select
              label="Project Type *"
              placeholder="Select project type"
              data={[
                { value: 'res', label: 'Residential' },
                { value: 'com', label: 'Commercial' },
              ]}
              {...form.getInputProps('projecttype')}
            />
          </Col>
          {/* Row 2: Dates */}
          <Col span={12} md={2}>
            <DateInput
              label="Property List Date *"
              valueFormat="YYYY-MM-DD"
              placeholder="Select list date"
              {...form.getInputProps('propertylistdate')}
              defaultValue={new Date()}
              clearable
            />
          </Col>
          <Col span={12} md={2}>

            <TextInput
              label="Possession On *"
              placeholder="Enter possessionon"
              {...form.getInputProps('possessionon')}
            />
          </Col>
          {/* Row 3: Developer & Location */}
          <Col span={12} md={2}>
            <Select
              label="Developer *"
              placeholder="Select Developer"
              data={developerOptions}
              value={form.values.developerid}
              onChange={(value) => form.setFieldValue('developerid', value)}
              searchable
              nothingFound="No options"
            />
          </Col>
          <Col span={12} md={4}>
            <TextInput
              label="Property Address *"
              placeholder="Enter property address"
              {...form.getInputProps('propertyaddress')}
            />
          </Col>
          <Col span={12} md={2}>
            <Select
              label="State *"
              placeholder="Select State"
              data={stateOptions}
              value={form.values.propertystateid}
              onChange={(value) => {
                form.setFieldValue('propertystateid', value);
                loadCities(value);
              }}
              searchable
              nothingFound="No options"
            />
          </Col>
          <Col span={12} md={2}>
            <Select
              label="City *"
              placeholder="Select city"
              data={cityOptions}
              value={form.values.propertycityid}
              onChange={(value) => form.setFieldValue('propertycityid', value)}
              searchable
              nothingFound="No options"
            />
          </Col>
          {/* Row 4: Category & RERA */}
          <Col span={12} md={4}>
            <MultiSelect
              label="Category"
              placeholder="Select Category"
              data={categoryOptions}
              value={form.values.category}
              onChange={(value) => form.setFieldValue('category', value)}
              searchable
              nothingFound="No options"
            />
          </Col>
          <Col span={12} md={2}>
            <TextInput
              label="RERA No"
              placeholder="e.g. P02400007209"
              {...form.getInputProps('rerano')}
            />
          </Col>
          {/* Row 5: Pricing & Area */}
          <Col span={12} md={1}>
            <TextInput
              label="Price Range From"
              placeholder="e.g. 1.25 Cr"
              {...form.getInputProps('pricerangefrom')}
            />
          </Col>
          <Col span={12} md={1}>
            <TextInput
              label="Price Range To"
              placeholder="e.g. 2.25 Cr"
              {...form.getInputProps('pricerangeto')}
            />
          </Col>
          <Col span={12} md={1}>
            <TextInput
              label="Area From"
              placeholder="e.g. 1500 SQ.FT"
              {...form.getInputProps('arearangefrom')}
            />
          </Col>
          <Col span={12} md={1}>
            <TextInput
              label="Area To"
              placeholder="e.g. 2000 SQ.FT"
              {...form.getInputProps('arearangeto')}
            />
          </Col>
          <Col span={12} md={2}>
            <TextInput
              label="Project Status"
              placeholder="New/Old..."
              {...form.getInputProps('projectstatus')}
            />
          </Col>
          <Col span={12} md={3}>
            <TextInput
              label="Project Configuration"
              placeholder="2.5, 3 & 4 BHK"
              {...form.getInputProps('propertyconfiguration')}
            />
          </Col>

          <Col span={12} md={1}>
            <TextInput
              label="Order"
              placeholder="1,2,3..."
              {...form.getInputProps('sortorder')}
            />
          </Col>









          <Col span={12} md={7}>
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              placeholder="Write property configuration..."
              modules={modules}
              formats={formats}
              style={{ minHeight: '300px' }}
            />
          </Col>

          {/* Row 8: Image Upload */}
          <Col span={12} md={5}>
            <ImageUploadWithPreview
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
              existingImagePath={existingImagePath}
              setExistingImagePath={setExistingImagePath}
              label="Upload Main Image (16:9 preview)"
              fullWidthPreview={true}
            />
          </Col>
        </Grid>
        <Box mt="md">
          <Flex justify="flex-end">
            <Button type="submit">{id ? 'Update' : 'Create'}</Button>
          </Flex>
        </Box>
      </form>
    </Box>
  );
}

export default OverviewTab;
